import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CommonTable from "../../../common/table/ComonTable";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../common/formfields/CustomSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faPlus, faDownload, faCheck, faClock, faTimes, faChevronRight, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { primaryColor, secondaryColor } from "../../../../utils/comon";
import apiServices from "../../../../services/apiService";

// Initial Dummy Data to seed localStorage if empty
const INITIAL_COMPLAINTS = [
    {
        id: 1,
        complaintCode: "CMP-1024",
        title: "Water leak in laundry room",
        categoryId: 2,
        categoryName: "Plumbing",
        priorityId: 1,
        priorityName: "Low",
        priorityCode: "LOW",
        status: "In Progress", // Open, In Progress, Close, Reject
        date: "20 AUG 2026",
        raisedBy: "Harsh Bhagat",
        unit: "B-204",
        description: "Significant water leakage observed near the washing machine outlet. Water is pooling on the floor and affecting the drywall. Requesting urgent maintenance to prevent further structural damage.",
        assignToRoleId: 3,
        assignToRoleName: "Site Admin",
        raisedDate: "12 Aug 2026, 10:30 AM",
        inProgressDate: "13 Aug 2026, 09:15 AM",
        resolvedDate: "",
        problemImages: [
            "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=150&auto=format&fit=crop&q=60",
            "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=150&auto=format&fit=crop&q=60",
            "https://images.unsplash.com/photo-1517646287270-a5a9ca602e5c?w=150&auto=format&fit=crop&q=60"
        ],
        resolutionImages: [
            "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=150&auto=format&fit=crop&q=60",
            "https://images.unsplash.com/photo-1581094288338-2314dddb7ecc?w=150&auto=format&fit=crop&q=60",
            "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=150&auto=format&fit=crop&q=60"
        ]
    },
    {
        id: 2,
        complaintCode: "CMP-1025",
        title: "Lift not working",
        categoryId: 5,
        categoryName: "HVAC", // custom categories
        priorityId: 3,
        priorityName: "High",
        priorityCode: "HIGH",
        status: "Close",
        date: "02 JUN 2026",
        raisedBy: "Aarav Sharma",
        unit: "A-501",
        description: "The primary passenger lift in Wing A has stopped responding on the 5th floor. Residents are forced to take the service lift.",
        assignToRoleId: 5,
        assignToRoleName: "Employee",
        raisedDate: "01 Jun 2026, 08:00 AM",
        inProgressDate: "01 Jun 2026, 11:30 AM",
        resolvedDate: "02 Jun 2026, 04:00 PM",
        problemImages: [
            "https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?w=150&auto=format&fit=crop&q=60"
        ],
        resolutionImages: [
            "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=150&auto=format&fit=crop&q=60"
        ]
    },
    {
        id: 3,
        complaintCode: "CMP-1026",
        title: "Conference hall AC unit failure",
        categoryId: 5,
        categoryName: "HVAC",
        priorityId: 4,
        priorityName: "Critical",
        priorityCode: "CRITICAL",
        status: "Open",
        date: "05 AUG 2026",
        raisedBy: "Neha Patel",
        unit: "C-102",
        description: "The AC unit in conference room 3 is making loud rattling noises and blowing warm air. Important client meetings are scheduled.",
        assignToRoleId: 2,
        assignToRoleName: "Super Admin",
        raisedDate: "05 Aug 2026, 02:00 PM",
        inProgressDate: "",
        resolvedDate: "",
        problemImages: [],
        resolutionImages: []
    },
    {
        id: 4,
        complaintCode: "CMP-1027",
        title: "IT port port-6 inactive",
        categoryId: 4,
        categoryName: "IT / Network",
        priorityId: 2,
        priorityName: "Medium",
        priorityCode: "MEDIUM",
        status: "Reject",
        date: "28 JUL 2026",
        raisedBy: "Rohan Das",
        unit: "D-304",
        description: "Wall LAN port number 6 is not getting any connection. Attempted port resetting but status remains disconnected.",
        assignToRoleId: 4,
        assignToRoleName: "Unit Admin",
        raisedDate: "27 Jul 2026, 09:00 AM",
        inProgressDate: "",
        resolvedDate: "",
        problemImages: [],
        resolutionImages: []
    }
];

const ComplaintList = () => {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const siteId = user?.siteId;
    const unitId = user?.branchorunitID;
    const roleId = Number(user?.roleId || user?.RoleId);

    const [complaints, setComplaints] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPriority, setSelectedPriority] = useState("All");
    const [selectedStatus, setSelectedStatus] = useState("All");

    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalRecords: 0
    });

    const fetchComplaints = async () => {
        try {
            setLoading(true);
            let url = `/CommComplaint/Complaint/getComplaints`;
            const params = [];

            // role specific parameters
            if (roleId === 3) {
                if (siteId) params.push(`siteId=${siteId}`);
            } else if (roleId === 4) {
                if (siteId) params.push(`siteId=${siteId}`);
                if (unitId) params.push(`unitId=${unitId}`);
            }

            // pagination parameters
            params.push(`pageNumber=${pagination.pageNumber}`);
            params.push(`pageSize=${pagination.pageSize}`);

            if (selectedPriority !== "All") {
                const priorityMap = { "Low": 1, "Medium": 2, "High": 3, "Critical": 4 };
                const pId = priorityMap[selectedPriority];
                if (pId) params.push(`priorityId=${pId}`);
            }

            if (selectedStatus !== "All") {
                params.push(`status=${selectedStatus.toLowerCase()}`);
            }

            if (params.length > 0) {
                url += `?${params.join("&")}`;
            }

            const response = await apiServices({
                method: "GET",
                url: url
            });
            if (response?.success) {
                const apiData = response?.data?.data || response?.data || [];
                setComplaints(apiData);
                const firstRecord = apiData[0];
                const total = firstRecord?.totalCount || apiData.length;
                setPagination(prev => ({
                    ...prev,
                    totalRecords: total
                }));
            }
        } catch (error) {
            console.error("Failed to fetch complaints:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComplaints();
    }, [pagination.pageNumber, pagination.pageSize, selectedPriority, selectedStatus]);

    const filteredComplaints = complaints;

    const handleDelete = (row) => {
        if (window.confirm(`Are you sure you want to delete complaint CMP-${row.complaintId || row.id}?`)) {
            setComplaints(prev => prev.filter(item => (item.complaintId || item.id) !== (row.complaintId || row.id)));
        }
    };

    const handleEdit = (row) => {
        navigate(`edit/${row.complaintId || row.id}`);
    };

    // Columns configuration for CommonTable
    const tableHeaders = [
        { key: "no", label: "NO", align: "center" },
        { key: "complaintCode", label: "ID", align: "left" },
        { key: "title", label: "TITLE", align: "left" },
        { key: "categoryName", label: "CATEGORY", align: "center" },
        { key: "status", label: "STATUS", align: "center" },
        { key: "actions", label: "ACTIONS", align: "center" }
    ];

    const getPriorityStyle = (priorityCode) => {
        const pc = (priorityCode || "").toUpperCase();
        switch (pc) {
            case "LOW":
                return "bg-green-100 text-green-700 border border-green-200 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full";
            case "MEDIUM":
                return "bg-yellow-100 text-yellow-700 border border-yellow-200 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full";
            case "HIGH":
                return "bg-orange-100 text-orange-700 border border-orange-200 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full";
            case "CRITICAL":
                return "bg-red-100 text-red-700 border border-red-200 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full";
            default:
                return "bg-gray-100 text-gray-700 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full";
        }
    };

    const getStatusStyle = (status) => {
        const s = (status || "").toLowerCase();
        switch (s) {
            case "open":
                return "bg-gray-100 dark:bg-actionitembg text-gray-700 dark:text-textsecondary border border-gray-200 dark:border-bordercolor text-xs font-semibold px-3 py-1 rounded-full";
            case "in progress":
            case "inprogress":
                return "bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-900/50 text-xs font-semibold px-3 py-1 rounded-full";
            case "close":
            case "closed":
                return "bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-900/50 text-xs font-semibold px-3 py-1 rounded-full";
            case "reject":
            case "rejected":
                return "bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-900/50 text-xs font-semibold px-3 py-1 rounded-full";
            default:
                return "bg-gray-100 dark:bg-actionitembg text-gray-700 dark:text-textsecondary text-xs font-semibold px-3 py-1 rounded-full border border-gray-200 dark:border-bordercolor";
        }
    };

    const renderCell = (key, row, idx) => {
        if (key === "no") {
            return <span className="font-semibold text-xs text-gray-700 dark:text-textsecondary">{idx + 1}</span>;
        }
        if (key === "complaintCode") {
            return <span className="font-bold text-xs text-[#0F172A] dark:text-textprimary">{row.complaintId ? `CMP-${row.complaintId}` : row.complaintCode}</span>;
        }
        if (key === "title") {
            return <span className="font-bold text-xs text-[#0F172A] dark:text-textprimary">{row.complaintTitle || row.title}</span>;
        }
        if (key === "status") {
            return <span className={getStatusStyle(row.status)}>{row.status}</span>;
        }
        return <span className="text-tabletext text-xs font-semibold">{row[key] || "—"}</span>;
    };

    // Render expanded details sub-row
    const expandedRowRender = (row) => {
        const normStatus = (row.status || "").toLowerCase();

        // Extract history action times
        const raisedDate = row.createdOn
            ? new Date(row.createdOn).toLocaleString("en-US", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).toUpperCase()
            : row.raisedDate;

        const inProgressLog = row.history?.find(h => (h.newStatus || "").toLowerCase() === "in progress" || (h.newStatus || "").toLowerCase() === "inprogress");
        const inProgressDate = inProgressLog
            ? new Date(inProgressLog.modifiedOn).toLocaleString("en-US", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).toUpperCase()
            : row.inProgressDate;

        const closedLog = row.history?.find(h => ["close", "closed", "resolved", "reject", "rejected"].includes((h.newStatus || "").toLowerCase()));
        const resolvedDate = closedLog
            ? new Date(closedLog.modifiedOn).toLocaleString("en-US", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).toUpperCase()
            : row.resolvedDate;

        // Steps tracking configuration
        // Progress Steps: Raised -> In Progress -> Resolved/Closed
        const steps = [
            { label: "Complaint Raised", date: raisedDate, completed: true },
            { label: "In Progress", date: inProgressDate, completed: normStatus === "in progress" || normStatus === "inprogress" || normStatus === "close" || normStatus === "closed" || normStatus === "resolved" },
            { label: (normStatus === "reject" || normStatus === "rejected") ? "Rejected" : "Resolved", date: (normStatus === "close" || normStatus === "closed" || normStatus === "resolved" || normStatus === "reject" || normStatus === "rejected") ? resolvedDate : "", completed: normStatus === "close" || normStatus === "closed" || normStatus === "resolved" || normStatus === "reject" || normStatus === "rejected" }
        ];

        const raisedByName = row.userName || row.raisedBy || "—";
        const unitName = row.unitName || row.unit || "—";
        const displayDate = row.createdOn
            ? new Date(row.createdOn).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase()
            : row.date;

        const resolvedImagesList = row.resolutionImages || [];
        const problemImagesList = row.images ? row.images.map(img => img.image_url) : (row.problemImages || []);
        const priorityCodeVal = row.priorityCode || (row.priorityName || "").toUpperCase();

        return (
            <div className="p-2 bg-gray-50/50 dark:bg-actionitembg  border border-gray-100 dark:border-bordercolor text-[#0F172A] dark:text-textprimary">
                <div className="max-w-[1400px] mx-auto space-y-2">

                    {/* Sub Row details grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 bg-white dark:bg-cardbg border border-gray-100 dark:border-bordercolor rounded-xl p-4 shadow-sm">
                        <div>
                            <span className="block text-[10px] uppercase font-bold text-gray-400 dark:text-textsecondary tracking-wider">Raised By</span>
                            <span className="block text-sm font-semibold text-gray-800 dark:text-textprimary mt-1">{raisedByName}</span>
                        </div>
                        <div>
                            <span className="block text-[10px] uppercase font-bold text-gray-400 dark:text-textsecondary tracking-wider">Unit/Flat</span>
                            <span className="block text-sm font-semibold text-gray-800 dark:text-textprimary mt-1">{unitName}</span>
                        </div>
                        <div>
                            <span className="block text-[10px] uppercase font-bold text-gray-400 dark:text-textsecondary tracking-wider">Category</span>
                            <span className="block text-sm font-semibold text-gray-800 dark:text-textprimary mt-1">{row.categoryName}</span>
                        </div>
                        <div>
                            <span className="block text-[10px] uppercase font-bold text-gray-400 dark:text-textsecondary tracking-wider">Priority</span>
                            <span className={`inline-block text-xs font-semibold mt-1 ${priorityCodeVal === "LOW" ? "text-green-600 dark:text-green-400" : priorityCodeVal === "MEDIUM" ? "text-yellow-600 dark:text-yellow-400" : priorityCodeVal === "HIGH" ? "text-orange-600 dark:text-orange-400" : "text-red-600 dark:text-red-400"}`}>{row.priorityName}</span>
                        </div>
                        <div>
                            <span className="block text-[10px] uppercase font-bold text-gray-400 dark:text-textsecondary tracking-wider">Status</span>
                            <span className={`inline-block text-xs font-semibold mt-1 ${row.status === "Close" || row.status === "Closed" || row.status === "resolved" ? "text-green-600 dark:text-green-400" : row.status === "In Progress" || row.status === "inprogress" ? "text-blue-600 dark:text-blue-400" : row.status === "Reject" || row.status === "rejected" ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-textsecondary"}`}>{row.status}</span>
                        </div>
                        <div>
                            <span className="block text-[10px] uppercase font-bold text-gray-400 dark:text-textsecondary tracking-wider">Date</span>
                            <span className="block text-sm font-semibold text-gray-800 dark:text-textprimary mt-1">{displayDate}</span>
                        </div>
                    </div>

                    {/* Description card */}
                    <div className="bg-white dark:bg-cardbg border border-gray-100 dark:border-bordercolor rounded-xl p-4 shadow-sm">
                        <span className="block text-[10px] uppercase font-bold text-gray-400 dark:text-textsecondary tracking-wider">Description</span>
                        <p className="text-sm text-gray-700 dark:text-textprimary leading-relaxed mt-2">{row.description}</p>
                    </div>

                    {/* Images & Progress Stepper */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                        {/* Images grid */}
                        <div className="space-y-4">
                            {/* Problem Images */}
                            <div className="bg-white dark:bg-cardbg border border-gray-100 dark:border-bordercolor rounded-xl p-4 shadow-sm">
                                <span className="block text-[10px] uppercase font-bold text-gray-400 dark:text-textsecondary tracking-wider mb-3">Problem Images (Resident)</span>
                                {problemImagesList && problemImagesList.length > 0 ? (
                                    <div className="flex flex-wrap gap-2.5">
                                        {problemImagesList.map((img, index) => (
                                            <a href={img} target="_blank" rel="noopener noreferrer" key={index}>
                                                <img src={img} alt="Problem thumbnail" className="w-16 h-16 rounded-lg object-cover border border-gray-200 dark:border-bordercolor hover:opacity-85 transition-opacity" />
                                            </a>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-xs text-gray-400 dark:text-textsecondary font-medium">No images uploaded.</span>
                                )}
                            </div>

                            {/* Resolution Images */}
                            <div className="bg-white dark:bg-cardbg border border-gray-100 dark:border-bordercolor rounded-xl p-4 shadow-sm">
                                <span className="block text-[10px] uppercase font-bold text-gray-400 dark:text-textsecondary tracking-wider mb-3">Resolution Images (Admin)</span>
                                {resolvedImagesList && resolvedImagesList.length > 0 ? (
                                    <div className="flex flex-wrap gap-2.5">
                                        {resolvedImagesList.map((img, index) => (
                                            <a href={img} target="_blank" rel="noopener noreferrer" key={index}>
                                                <img src={img} alt="Resolution thumbnail" className="w-16 h-16 rounded-lg object-cover border border-gray-200 dark:border-bordercolor hover:opacity-85 transition-opacity" />
                                            </a>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-xs text-gray-400 dark:text-textsecondary font-medium">No resolution images uploaded.</span>
                                )}
                            </div>
                        </div>

                        {/* Progress Tracker */}
                        <div className="bg-white dark:bg-cardbg border border-gray-100 dark:border-bordercolor rounded-xl p-4 shadow-sm animate-fade-in">
                            <span className="block text-[10px] uppercase font-bold text-gray-400 dark:text-textsecondary tracking-wider mb-6">Complaint Progress</span>

                            {/* Stepper container */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center relative gap-6 sm:gap-2">
                                {/* Connecting line for horizontal stepper */}
                                <div className="hidden sm:block absolute left-[16.66%] right-[16.66%] h-0.5 bg-gray-200 dark:bg-actionitembg z-0 top-[15px]"></div>

                                {steps.map((step, idx) => (
                                    <div key={idx} className="flex sm:flex-col items-center gap-4 sm:gap-2 z-10 flex-1 relative">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors duration-300
                                            ${step.completed
                                                ? "bg-red-500 border-red-500 text-white"
                                                : "bg-white dark:bg-cardbg border-gray-200 dark:border-bordercolor text-gray-300 dark:text-textsecondary"
                                            }
                                        `}>
                                            {step.completed ? (
                                                <FontAwesomeIcon icon={faCheck} className="text-xs" />
                                            ) : (
                                                <div className="w-2.5 h-2.5 rounded-full bg-gray-200 dark:bg-actionitembg"></div>
                                            )}
                                        </div>
                                        <div className="text-left sm:text-center">
                                            <span className="block text-xs font-bold text-gray-800 dark:text-textprimary">{step.label}</span>
                                            {step.date ? (
                                                <span className="block text-[10px] text-gray-400 dark:text-textsecondary mt-0.5">{step.date}</span>
                                            ) : (
                                                <span className="block text-[10px] text-gray-300 dark:text-textsecondary/50 mt-0.5">—</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>

                </div>
            </div>
        );
    };

    const categoryOptions = [
        { label: "All Categories", value: "All" },
        { label: "Electrical", value: "Electrical" },
        { label: "Plumbing", value: "Plumbing" },
        { label: "Housekeeping", value: "Housekeeping" },
        { label: "IT / Network", value: "IT / Network" },
        { label: "HVAC", value: "HVAC" },
        { label: "Other", value: "Other" }
    ];

    const priorityOptions = [
        { label: "All Priorities", value: "All" },
        { label: "Low", value: "Low" },
        { label: "Medium", value: "Medium" },
        { label: "High", value: "High" },
        { label: "Critical", value: "Critical" }
    ];

    const statusOptions = [
        { label: "All Status", value: "All" },
        { label: "Open", value: "Open" },
        { label: "In Progress", value: "In Progress" },
        { label: "Close", value: "Close" },
        { label: "Reject", value: "Reject" }
    ];

    const handlePriorityChange = (val) => {
        setSelectedPriority(val);
        setPagination(prev => ({ ...prev, pageNumber: 1 }));
    };

    const handleStatusChange = (val) => {
        setSelectedStatus(val);
        setPagination(prev => ({ ...prev, pageNumber: 1 }));
    };

    const tableActions = (
        <div className="flex flex-col md:flex-row items-center gap-3 text-xs w-full md:w-auto">
            {/* Priority Select */}
            <div className="w-full md:w-[150px]">
                <CustomSelect
                    options={priorityOptions}
                    value={priorityOptions.find(opt => opt.value === selectedPriority)}
                    onChange={(opt) => handlePriorityChange(opt ? opt.value : "All")}
                    placeholder="All Priorities"
                />
            </div>

            {/* Status Select */}
            <div className="w-full md:w-[150px]">
                <CustomSelect
                    options={statusOptions}
                    value={statusOptions.find(opt => opt.value === selectedStatus)}
                    onChange={(opt) => handleStatusChange(opt ? opt.value : "All")}
                    placeholder="All Status"
                />
            </div>

            {/* Export Button */}
            <button
                onClick={() => alert("Data Exported Successfully")}
                className="flex items-center gap-2 border border-gray-200 dark:border-bordercolor bg-white dark:bg-actionitembg text-gray-600 dark:text-textsecondary px-4 py-2 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-[#2B2B2D] font-semibold h-[38px] transition-colors"
            >
                <FontAwesomeIcon icon={faDownload} />
                Export
            </button>
        </div>
    );

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />

            <PageHeadingSection
                Navbar={true}
                mainHeading="Complaints Management"
                subHeading="View and manage user complaints."
                primaryBtnText="Raise Complaint"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() => navigate("add")}
            />

            {/* Complaints Table */}
            <div className="flex-1 w-full mt-4">
                <CommonTable
                    headers={tableHeaders}
                    data={filteredComplaints.map((row, idx) => ({
                        ...row,
                        pageIndex: idx,
                        uniqueKey: `complaint-${row.complaintId || row.id || idx}`
                    }))}
                    renderCell={(key, row) => renderCell(key, row, row.pageIndex)}
                    expandedRowRender={expandedRowRender}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    tableHeading="Complaint Logs"
                    headerAction={tableActions}
                    pagination={pagination}
                    onPageChange={(page) => setPagination(prev => ({ ...prev, pageNumber: page }))}
                    onLimitChange={(limit) => setPagination(prev => ({ ...prev, pageSize: limit, pageNumber: 1 }))}
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default ComplaintList;
